import type { ChoreoLink } from './calendar'
import { lessonSessionOf } from './privateLessons'

export type TeacherSession = {
  occurrenceId: string
  studioClassId: string
  className: string
  routineName: string | null
  date: string
  startTime: string | null
  endTime: string | null
  roomName: string | null
  /** The studio colour-codes its rooms; every calendar reads the same value (D-176). */
  roomColor: string | null
  enrolled: number
  marked: number
  isCancelled: boolean
  /** False when she is covering somebody else's class (D-12). */
  isMine: boolean
  musicUrl: string | null

  /**
   * A room booking or a private lesson rather than a class (D-80).
   *
   * The server has sent this all along and the type did not carry it, so every
   * screen reading her sessions treated a reservation as a class. Declaring
   * cover on one 404s: a booking has no `ClassOccurrence` behind it.
   */
  isPrivate?: boolean

  /**
   * A private lesson with attendance behind it (D-729): a technique makeup is a
   * session of the dancer's class, so it has a register where a plain booking
   * has none.
   */
  hasAttendance?: boolean

  /**
   * Who a private lesson is with, when it is one.
   *
   * Reported from testing: the Today panel read *"Private lesson, 2-Yellow
   * Room"* and nothing else, so a teacher could see she had one at six and not
   * who it was for — which is the whole content of a private. The server has
   * sent this all along (D-372 settled that a teacher may see the name); the
   * type did not carry it, so the panel could not say it.
   *
   * Null on a class: a roster is not a name to put in a heading.
   */
  dancerNames?: string | null

  /**
   * The sessions behind a private lesson, by id (D-730, D-733).
   *
   * **A private's `occurrenceId` is its *booking*, not a session** — the server
   * says so where it builds the row, and attendance attaches to a session and
   * never to a booking (A-1, D-80). So anything that wants this row's register
   * has to come through here. The server has sent it all along; the type did
   * not carry it, which is the same shape of gap `dancerNames` above was.
   *
   * Empty for a class row and for a plain private.
   */
  sessionIds?: string[]
}

export type TeacherHome = {
  greeting: string
  today: string
  todaySessions: TeacherSession[]
  upcoming: TeacherSession[]
  /** Her own past registers still to be finished (D-118). */
  outstanding: number
  /**
   * Dancers reported out of her *next* session (D-188).
   *
   * Dancers rather than sessions, and one class rather than a week: the number
   * has to mean empty spots on the floor of the class she is about to teach.
   */
  reportedOut: number
  /** Which session `reportedOut` counts, so the screen can name the day. */
  nextSessionDate: string | null
  /**
   * Privates she taught this season that nobody was ever asked to pay for
   * (D-864).
   *
   * A closed list: a booking has raised its own money ask since 2026-09-14, so
   * this only ever holds lessons from before that and empties as she deals with
   * them. The panel is hidden when it is empty rather than saying "none", which
   * is why it can live on the screen she opens most.
   */
  unpaidPrivates: UnpaidPrivate[]
}

/** One private lesson still to be asked about (D-864). */
export type UnpaidPrivate = {
  bookingId: string
  date: string
  start: string
  /** The dancers on the booking, already joined for reading. */
  dancers: string
  room: string
}

export type TeacherClass = {
  studioClassId: string
  name: string
  routineName: string | null
  dayOfWeek: string
  startTime: string | null
  endTime: string | null
  roomName: string | null
  /** The studio colour-codes its rooms; every calendar reads the same value (D-176). */
  roomColor: string | null
  enrolled: number
  musicUrl: string | null
  /** The practice videos she added for families (D-541), in order, so she can check they open. */
  choreoLinks: ChoreoLink[]
}

export async function fetchTeacherHome(): Promise<TeacherHome> {
  const res = await fetch('/api/teacher')
  if (!res.ok) throw new Error(`Could not load your schedule (${res.status})`)
  return res.json()
}

export async function fetchTeacherClasses(): Promise<TeacherClass[]> {
  const res = await fetch('/api/teacher/classes')
  if (!res.ok) throw new Error(`Could not load your classes (${res.status})`)
  return res.json()
}

/**
 * Names the routine and sets its music link (D-319).
 *
 * The refusal is a sentence the server wrote — a link with no scheme is the
 * one mistake this makes, and "Paste the whole address" is more use than
 * "400 Bad Request".
 */
export async function saveRoutine(
  classId: string,
  routineName: string | null,
  musicUrl: string | null,
  /** Unlisted YouTube links for families (D-541), in order; empty clears them. Blank rows are dropped by the server. */
  choreoLinks: ChoreoLink[] = [],
): Promise<void> {
  const res = await fetch(`/api/teacher/classes/${classId}/routine`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ routineName, musicUrl, choreoLinks }),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)
    throw new Error(problem?.detail ?? problem?.title ?? `Could not save (${res.status})`)
  }
}

/**
 * One night of her own class, moved to another day or another hour (D-698,
 * pilot item 24).
 *
 * <b>Her own door, not the studio's.</b> `/api/admin/.../room` does the same
 * work behind the same implementation and is gated on `ManageClasses`, which a
 * teacher will never hold — so the capability that decides this is
 * `MoveOwnSessions`, and the relationship check behind this route decides
 * which classes (D-286, D-699).
 *
 * <b>It may name a room, and once did not</b> (D-893, amending D-700). The
 * night keeps whichever room it is already in unless she says otherwise —
 * omitting the field is still *keep it*, which is what the server reads a null
 * as. D-700 withheld the choice on the reasoning that where a class meets is
 * the studio's to arrange; what that left her with was a dead end, because her
 * Tuesday room is somebody else's on Monday and the refusal named no way out.
 * She already holds `BookRooms`, so she could reserve that room outright.
 *
 * Every refusal here is a sentence the server wrote — *"this class already
 * meets on Wed 8 Oct"*, *"that night is cancelled"* — and each one is the only
 * thing that can explain why the move did not happen.
 */
export async function moveMyClassNight(
  classId: string,
  occurrenceId: string,
  when: { date?: string; startTime?: string; endTime?: string; roomId?: string },
): Promise<void> {
  const res = await fetch(`/api/teacher/classes/${classId}/occurrences/${occurrenceId}/move`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    // `roomId: null` first so an unchanged room stays unchanged: the server
    // reads a null as *leave it where it is* (D-700), and `when` overrides it
    // only when she picked one.
    body: JSON.stringify({ roomId: null, rest: false, ...when }),
  })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)

    throw new Error(
      problem?.error ?? problem?.detail ?? problem?.title ?? `Could not move that (${res.status})`,
    )
  }
}

/**
 * Ask the family to pay for a private nobody was ever asked about (D-864).
 *
 * Resolves `false` when the question was already there — the automatic raise got
 * it, or she double-tapped. That is not an error: the studio is in the state she
 * asked for, and the row leaves the list either way.
 */
export async function askToBePaidForPrivate(bookingId: string): Promise<boolean> {
  const res = await fetch(`/api/teacher/privates/${bookingId}/payment`, { method: 'POST' })

  if (!res.ok) {
    const problem = await res.json().catch(() => null)

    throw new Error(
      problem?.detail ?? problem?.title ?? `Could not ask for that (${res.status})`,
    )
  }

  const body = await res.json().catch(() => null)
  return body?.asked === true
}

/** Whether a register still needs finishing. */
export function needsMarking(session: TeacherSession): boolean {
  /*
   * **A private can never be marked, so it is never unfinished** (D-369).
   *
   * A booking arrives as a session with its dancers in `enrolled` and `marked`
   * written as the literal 0 — attendance keys on a `ClassOccurrence` and a
   * booking has none (A-1, D-80), so there is nothing for a mark to attach to. Without this line every private a teacher has ever
   * taught counts as an unfinished register, for ever.
   *
   * **Unless it has one** (D-729): a technique makeup is a session of the
   * dancer's class, and the server sizes it as one dancer.
   */
  if (session.isPrivate && !session.hasAttendance) return false

  return !session.isCancelled && session.enrolled > 0 && session.marked < session.enrolled
}

/**
 * Which session's register this row opens, or null when it has none.
 *
 * <b>A class row is its own session; a private's row is its booking</b> (D-733).
 * `occurrenceId` carries a `RoomBooking` id for a private, so handing it
 * straight to the roster asks for a session that does not exist — the
 * schedule already knows this and goes through `lessonSessionOf`, and a second
 * rule here would be the drift that function exists to prevent.
 *
 * Null for a plain private, which has no register at all (D-369), and for a
 * makeup whose sessions the server did not send — a control that opens nothing
 * is worse than no control.
 */
export function registerOf(session: TeacherSession): string | null {
  if (session.isPrivate) return lessonSessionOf(session)
  return session.occurrenceId
}

/**
 * What to call a class on a teacher's screen.
 *
 * The stage name when there is one — she calls it "Golden Huntrix", not
 * `COMP_OPEN | MO 545 | 13/14 | LINE | COMP` (D-115).
 *
 * Takes either shape because a session names it `className` and a class names
 * it `name`; one label function beats two that could drift apart.
 */
export function classLabel(
  item: { routineName: string | null } & ({ className: string } | { name: string }),
): string {
  const fallback = 'className' in item ? item.className : item.name
  return item.routineName?.trim() || fallback
}

/**
 * Groups upcoming sessions by date.
 *
 * A teacher thinks in evenings — "Tuesday I have three" — not in classes.
 */
export function byDay(sessions: TeacherSession[]): { date: string; sessions: TeacherSession[] }[] {
  const days: { date: string; sessions: TeacherSession[] }[] = []

  for (const session of sessions) {
    const day = days.find((d) => d.date === session.date)
    if (day) day.sessions.push(session)
    else days.push({ date: session.date, sessions: [session] })
  }

  return days
}


/**
 * "tomorrow", "on Thursday", or "on 8 September".
 *
 * Anchored on the studio's own today rather than the browser clock, so it
 * cannot disagree with the rest of the screen on a device in another timezone
 * or one whose clock is simply wrong (D-68).
 */
export function friendlyDate(date: string, today: string): string {
  const days = dayGap(today, date)

  if (days === 1) return 'tomorrow'
  if (days > 1 && days < 7) {
    const weekday = new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long' })
    return `on ${weekday}`
  }

  const when = new Date(`${date}T00:00:00`)
  return `on ${when.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`
}

/** Whole days between two `yyyy-mm-dd` dates, UTC-anchored so DST cannot shift it (D-68). */
function dayGap(from: string, to: string): number {
  const parse = (value: string) => {
    const [y, m, d] = value.split('-').map(Number)
    return Date.UTC(y, m - 1, d)
  }

  return Math.round((parse(to) - parse(from)) / 86_400_000)
}
